import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  serviceDeskTicketCreateListQueryKeyWith,
} from "@sd/services/APIs";
import {
  ASSIGNED_SELECT_NONE,
  ADMIN_RECENT_TICKETS_FROM_QUERY,
  isAdminRecentTicketsListContext,
  parseAdminTicketListDeepLinkSearchParams,
} from "@sd/lib/adminTicketUtils";
import {
  emptyCreatedDateFilter,
  getDefaultCreatedDateRange,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { emptyTicketFilterState, DEFAULT_MODULE_FILTER } from "@sd/lib/ticketFilterDefaults";
import { sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import { buildTicketCreateListFilterParams } from "@sd/lib/ticketCreateListFilter";
import { resolvePtsModuleFilterProjectId } from "@sd/hooks/usePtsModuleFilterOptions";
import { parseTrackerDrillDownSearchParams, isTrackerReportReturnContext, TRACKER_REPORT_FROM_QUERY, TRACKER_REPORT_FROM_VALUE, buildTrackerReportReturnSearchParams } from "@sd/lib/trackerReportDrillDown";
import {
  applyStatusDashboardListContext,
  applyTrackerReportListContext,
  assigneeFiltersFromTrackerAssigneeFilter,
  persistStatusDashboardTicketListContext,
  persistTrackerReportProjectId,
  persistTrackerReportReturnFilters,
  persistTrackerReportTicketListContext,
  readStatusDashboardTicketListContext,
  readTrackerReportProjectId,
  readTrackerReportReturnFilters,
  readTrackerReportTicketListContext,
  trackerReportListContextFromDrillDown,
} from "@sd/lib/ticketListDrillDownStorage";
import {
  emptyStatusDashboardFilters,
  type StatusDashboardFilters,
} from "@sd/lib/statusDashboardApi";
import {
  mergeTrackerReportFilters,
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";
import {
  buildStatusDashboardReturnSearchParams,
  isStatusDashboardReturnContext,
  parseStatusDashboardDrillDownSearchParams,
  parseStatusDashboardReturnContext,
} from "@sd/lib/statusDashboardDrillDown";
import {
  isDebouncedValuePending,
  useDebouncedValue,
} from "@sd/hooks/useDebouncedValue";
import {
  adminTicketsRefreshStorageScope,
  adminToolbarScope,
  DEFAULT_CREATE_UPDATE_FILTER,
  stripToolbarFiltersFromSearchParams,
  type CreateUpdateFilter,
} from "@sd/lib/ticketListFilterStorage";
import { useTicketToolbarFiltersRecoil } from "@sd/hooks/useTicketToolbarFiltersRecoil";

const SEARCH_DEBOUNCE_MS = 400;
const FILTER_DEBOUNCE_MS = 250;

export interface AdminTicketFilters {
  search: string;
  setSearch: (v: string) => void;
  statusFilters: string[];
  setStatusFilters: (v: string[]) => void;
  priorityFilters: string[];
  setPriorityFilters: (v: string[]) => void;
  categoryFilters: string[];
  setCategoryFilters: (v: string[]) => void;
  typeFilter: string;
  setTypeFilter: (v: string) => void;
  assigneeFilters: string[];
  setAssigneeFilters: (v: string[]) => void;
  projectFilters: string[];
  setProjectFilters: (v: string[]) => void;
  requesterFilters: string[];
  setRequesterFilters: (v: string[]) => void;
  createdDateFilter: CreatedDateFilterState;
  setCreatedDateFilter: (v: CreatedDateFilterState) => void;
  vipFilter: string;
  setVipFilter: (v: string) => void;
  slaFilter: string;
  setSlaFilter: (v: string) => void;
  moduleFilter: string;
  setModuleFilter: (v: string) => void;
  createUpdateFilter: CreateUpdateFilter;
  setCreateUpdateFilter: (v: CreateUpdateFilter) => void;
  resetFilters: () => void;
  pageNum: number;
  setPageNum: (n: number | ((prev: number) => number)) => void;
  pageSize: number;
  setPageSize: (n: number) => void;
  ticketGridFilterParams: Record<string, unknown>;
  ticketGridQueryKey: readonly unknown[];
  /** True while UI filters changed but API query has not caught up yet. */
  isFilterDebouncing: boolean;
  /** Opened from Tracker Report drill-down — show back link. */
  fromTrackerReport: boolean;
  reportReturnProjectId: string;
  trackerReportReturnFilters: TrackerReportFilters;
  /** Opened from Dashboard Project Status card — show back link. */
  fromStatusDashboard: boolean;
  statusDashboardReturnFilters: StatusDashboardFilters;
}

export function useAdminTicketFilters(): AdminTicketFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    assigneeFilters,
    setAssigneeFilters,
    projectFilters,
    setProjectFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    vipFilter,
    setVipFilter,
    slaFilter,
    setSlaFilter,
    moduleFilter,
    setModuleFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    resetToolbarFilters,
  } = useTicketToolbarFiltersRecoil(
    adminToolbarScope(),
    true,
    adminTicketsRefreshStorageScope()
  );

  const [pageSize, setPageSize] = useState(100);
  const [pageNum, setPageNum] = useState(1);
  const [notCloseFilter, setNotCloseFilter] = useState(false);

  useEffect(() => {
    if (!resolvePtsModuleFilterProjectId(projectFilters)) {
      if (moduleFilter !== DEFAULT_MODULE_FILTER) {
        setModuleFilter(DEFAULT_MODULE_FILTER);
      }
    }
  }, [projectFilters, moduleFilter, setModuleFilter]);

  const [fromTrackerReport, setFromTrackerReport] = useState(() =>
    searchParams.get(TRACKER_REPORT_FROM_QUERY) === TRACKER_REPORT_FROM_VALUE
  );
  const [fromStatusDashboard, setFromStatusDashboard] = useState(() =>
    isStatusDashboardReturnContext(searchParams)
  );
  const [statusDashboardReturnFilters, setStatusDashboardReturnFilters] =
    useState<StatusDashboardFilters>(() =>
      parseStatusDashboardReturnContext(searchParams) ??
        emptyStatusDashboardFilters()
    );
  const [reportReturnProjectId, setReportReturnProjectId] = useState(() =>
    searchParams.get("project")?.trim() || readTrackerReportProjectId() || ""
  );
  const [trackerReportReturnFilters, setTrackerReportReturnFilters] =
    useState<TrackerReportFilters>(() =>
      readTrackerReportReturnFilters() ?? mergeTrackerReportFilters(null)
    );

  useEffect(() => {
    const returnCtx = isTrackerReportReturnContext(searchParams);
    const statusDashboardCtx = isStatusDashboardReturnContext(searchParams);
    const trackerDrillDown = parseTrackerDrillDownSearchParams(searchParams);
    const statusDrillDown =
      parseStatusDashboardDrillDownSearchParams(searchParams);
    const drillDown = trackerDrillDown ?? statusDrillDown;

    const listDeepLink = parseAdminTicketListDeepLinkSearchParams(searchParams);

    if (!drillDown && isAdminRecentTicketsListContext(searchParams)) {
      setNotCloseFilter(true);
      setPageNum(1);
      const clean = stripToolbarFiltersFromSearchParams(searchParams);
      clean.delete(ADMIN_RECENT_TICKETS_FROM_QUERY);
      if (clean.toString() !== searchParams.toString()) {
        setSearchParams(clean, { replace: true });
      }
      return;
    }

  if (!drillDown) {
      if (returnCtx) {
        setFromTrackerReport(true);
        setReportReturnProjectId(returnCtx.projectId);
        const savedReturnFilters = readTrackerReportReturnFilters();
        if (savedReturnFilters) {
          setTrackerReportReturnFilters(savedReturnFilters);
        }
        const savedList = readTrackerReportTicketListContext();
        if (savedList) {
          applyTrackerReportListContext(savedList, {
            setProjectFilters,
            setStatusFilters,
            setTypeFilter,
            setAssigneeFilters,
            setCreatedDateFilter,
          });
        } else if (returnCtx.projectId) {
          setProjectFilters([returnCtx.projectId]);
        }
      } else {
        setFromTrackerReport(false);
      }
      setFromStatusDashboard(statusDashboardCtx);
      if (statusDashboardCtx) {
        const returnFilters =
          parseStatusDashboardReturnContext(searchParams) ??
          emptyStatusDashboardFilters();
        setStatusDashboardReturnFilters(returnFilters);
        const savedList = readStatusDashboardTicketListContext();
        if (savedList) {
          applyStatusDashboardListContext(savedList, {
            setProjectFilters,
            setStatusFilters,
            setCreatedDateFilter,
          });
        } else if (returnFilters.projectFilters.length > 0) {
          setProjectFilters(returnFilters.projectFilters);
          setCreatedDateFilter(returnFilters.createdDateFilter);
        }
      } else if (listDeepLink) {
        const defaults = emptyTicketFilterState();
        setSearch(defaults.search);
        setPriorityFilters(defaults.priorityFilters);
        setCategoryFilters(defaults.categoryFilters);
        setTypeFilter(defaults.typeFilter);
        setRequesterFilters(defaults.requesterFilters);
        setCreatedDateFilter(defaults.createdDateFilter);
        setVipFilter("all");
        setSlaFilter("all");
        setCreateUpdateFilter(DEFAULT_CREATE_UPDATE_FILTER);

        if (listDeepLink.unassigned) {
          setAssigneeFilters([ASSIGNED_SELECT_NONE]);
        } else if (listDeepLink.assignedTo) {
          setAssigneeFilters([listDeepLink.assignedTo]);
        } else {
          setAssigneeFilters(defaults.assigneeFilters);
        }

        setStatusFilters(
          listDeepLink.statusFilters.length > 0
            ? listDeepLink.statusFilters
            : defaults.statusFilters
        );

        if (listDeepLink.projectFilters.length > 0 && !returnCtx) {
          setProjectFilters(listDeepLink.projectFilters);
        } else {
          setProjectFilters(defaults.projectFilters);
        }

        setPageNum(1);
        const clean = stripToolbarFiltersFromSearchParams(searchParams);
        if (clean.toString() !== searchParams.toString()) {
          setSearchParams(clean, { replace: true });
        }
      } else {
        const clean = stripToolbarFiltersFromSearchParams(searchParams);
        if (clean.toString() !== searchParams.toString()) {
          setSearchParams(clean, { replace: true });
        }
      }
      return;
    }

    const defaults = emptyTicketFilterState();
    setSearch(defaults.search);
    setPriorityFilters(defaults.priorityFilters);
    setCategoryFilters(defaults.categoryFilters);
    setAssigneeFilters(defaults.assigneeFilters);
    setRequesterFilters(defaults.requesterFilters);
    setProjectFilters(
      drillDown.projectFilters.length > 0
        ? drillDown.projectFilters
        : defaults.projectFilters
    );
    setStatusFilters(
      sanitizeStatusFilterValues(
        drillDown.statusFilters.length > 0
          ? drillDown.statusFilters
          : defaults.statusFilters
      )
    );
    if (statusDrillDown) {
      setCreatedDateFilter(statusDrillDown.createdDateFilter);
      setTypeFilter(defaults.typeFilter);
      setFromStatusDashboard(true);
      setFromTrackerReport(false);
      setReportReturnProjectId("");
      const returnFilters: StatusDashboardFilters = {
        projectFilters: statusDrillDown.projectFilters,
        createdDateFilter: statusDrillDown.createdDateFilter,
      };
      setStatusDashboardReturnFilters(returnFilters);
      persistStatusDashboardTicketListContext({
        projectFilters: statusDrillDown.projectFilters,
        statusFilters: statusDrillDown.statusFilters,
        createdDateFilter: statusDrillDown.createdDateFilter,
      });
      setSearchParams(buildStatusDashboardReturnSearchParams(returnFilters), {
        replace: true,
      });
    } else {
      const trackerAssignee = trackerDrillDown!.assigneeFilter ?? "all";
      setAssigneeFilters(
        assigneeFiltersFromTrackerAssigneeFilter(trackerAssignee)
      );
      setCreatedDateFilter(
        trackerDrillDown!.createdDateFilter ?? defaults.createdDateFilter
      );
      setTypeFilter(
        trackerDrillDown!.typeFilter !== "all"
          ? trackerDrillDown!.typeFilter
          : defaults.typeFilter
      );
      setPageNum(1);

      const fromReport =
        searchParams.get(TRACKER_REPORT_FROM_QUERY) === TRACKER_REPORT_FROM_VALUE;
      const returnProject = drillDown.projectFilters[0] ?? "";
      const returnFilters: TrackerReportFilters = {
        projectFilters: drillDown.projectFilters,
        assigneeFilter: trackerAssignee,
        createdDateFilter: trackerDrillDown!.createdDateFilter ?? emptyCreatedDateFilter(),
      };
      setFromTrackerReport(fromReport);
      setFromStatusDashboard(false);
      setReportReturnProjectId(returnProject);
      setTrackerReportReturnFilters(returnFilters);

      if (fromReport) {
        if (returnProject) persistTrackerReportProjectId(returnProject);
        persistTrackerReportReturnFilters(returnFilters);
        persistTrackerReportTicketListContext(
          trackerReportListContextFromDrillDown({
            projectFilters: drillDown.projectFilters,
            statusFilters: drillDown.statusFilters,
            typeFilter:
              trackerDrillDown!.typeFilter !== "all"
                ? trackerDrillDown!.typeFilter
                : defaults.typeFilter,
            assigneeFilter: trackerAssignee,
            createdDateFilter:
              trackerDrillDown!.createdDateFilter ?? emptyCreatedDateFilter(),
          })
        );
        setSearchParams(buildTrackerReportReturnSearchParams(), {
          replace: true,
        });
      } else {
        setSearchParams({}, { replace: true });
      }
    }
    setPageNum(1);
  }, [searchParams, setSearchParams]);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const debouncedStatusFilters = useDebouncedValue(statusFilters, FILTER_DEBOUNCE_MS);
  const debouncedPriorityFilters = useDebouncedValue(priorityFilters, FILTER_DEBOUNCE_MS);
  const debouncedCategoryFilters = useDebouncedValue(categoryFilters, FILTER_DEBOUNCE_MS);
  const debouncedTypeFilter = useDebouncedValue(typeFilter, FILTER_DEBOUNCE_MS);
  const debouncedAssigneeFilters = useDebouncedValue(assigneeFilters, FILTER_DEBOUNCE_MS);
  const debouncedProjectFilters = useDebouncedValue(projectFilters, FILTER_DEBOUNCE_MS);
  const debouncedRequesterFilters = useDebouncedValue(requesterFilters, FILTER_DEBOUNCE_MS);
  const debouncedCreatedDateFilter = useDebouncedValue(
    createdDateFilter,
    FILTER_DEBOUNCE_MS
  );

  const isFilterDebouncing = useMemo(
    () =>
      isDebouncedValuePending(search, debouncedSearch) ||
      isDebouncedValuePending(statusFilters, debouncedStatusFilters) ||
      isDebouncedValuePending(priorityFilters, debouncedPriorityFilters) ||
      isDebouncedValuePending(categoryFilters, debouncedCategoryFilters) ||
      isDebouncedValuePending(typeFilter, debouncedTypeFilter) ||
      isDebouncedValuePending(assigneeFilters, debouncedAssigneeFilters) ||
      isDebouncedValuePending(projectFilters, debouncedProjectFilters) ||
      isDebouncedValuePending(requesterFilters, debouncedRequesterFilters) ||
      isDebouncedValuePending(createdDateFilter, debouncedCreatedDateFilter),
    [
      search,
      debouncedSearch,
      statusFilters,
      debouncedStatusFilters,
      priorityFilters,
      debouncedPriorityFilters,
      categoryFilters,
      debouncedCategoryFilters,
      typeFilter,
      debouncedTypeFilter,
      assigneeFilters,
      debouncedAssigneeFilters,
      projectFilters,
      debouncedProjectFilters,
      requesterFilters,
      debouncedRequesterFilters,
      createdDateFilter,
      debouncedCreatedDateFilter,
    ]
  );

  const resetFilters = useCallback(() => {
    resetToolbarFilters();
    // resetToolbarFilters restores default toolbar state including last-7-days date.
    setNotCloseFilter(false);
    setPageNum(1);
  }, [resetToolbarFilters]);

  useEffect(() => {
    setPageNum(1);
  }, [
    debouncedSearch,
    debouncedStatusFilters,
    debouncedPriorityFilters,
    debouncedCategoryFilters,
    debouncedTypeFilter,
    debouncedAssigneeFilters,
    debouncedProjectFilters,
    debouncedRequesterFilters,
    debouncedCreatedDateFilter,
    vipFilter,
    slaFilter,
    moduleFilter,
    createUpdateFilter,
  ]);

  const ticketGridFilterParams = useMemo(() => {
    const extra: Record<string, unknown> = {};
    if (vipFilter === "vip") extra.is_vip = true;
    if (slaFilter === "sla") extra.is_sla = true;
    if (notCloseFilter) extra.not_close = true;
    if (
      moduleFilter &&
      moduleFilter !== DEFAULT_MODULE_FILTER &&
      resolvePtsModuleFilterProjectId(debouncedProjectFilters)
    ) {
      extra.parent_task = moduleFilter;
    }

    return buildTicketCreateListFilterParams({
      search: debouncedSearch,
      page: pageNum,
      pageSize,
      statusFilters: debouncedStatusFilters,
      priorityFilters: debouncedPriorityFilters,
      categoryFilters: debouncedCategoryFilters,
      typeFilter: debouncedTypeFilter,
      projectFilters: debouncedProjectFilters,
      assigneeFilters: debouncedAssigneeFilters,
      unassignedFilterValue: ASSIGNED_SELECT_NONE,
      requesterFilters: debouncedRequesterFilters,
      createdDateFilter: debouncedCreatedDateFilter,
      createUpdateFilter,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    });
  }, [
    debouncedSearch,
    pageNum,
    pageSize,
    debouncedStatusFilters,
    debouncedPriorityFilters,
    debouncedCategoryFilters,
    debouncedTypeFilter,
    debouncedAssigneeFilters,
    debouncedProjectFilters,
    debouncedRequesterFilters,
    debouncedCreatedDateFilter,
    vipFilter,
    slaFilter,
    notCloseFilter,
    moduleFilter,
    createUpdateFilter,
  ]);

  const ticketGridQueryKey = useMemo(
    () =>
      serviceDeskTicketCreateListQueryKeyWith(
        ticketGridFilterParams,
        pageNum,
        pageSize,
        "admin-panel",
        // Explicit segment so filter change always changes the React Query key.
        createUpdateFilter
      ),
    [ticketGridFilterParams, pageNum, pageSize, createUpdateFilter]
  );

  return {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    assigneeFilters,
    setAssigneeFilters,
    projectFilters,
    setProjectFilters,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    vipFilter,
    setVipFilter,
    slaFilter,
    setSlaFilter,
    moduleFilter,
    setModuleFilter,
    createUpdateFilter,
    setCreateUpdateFilter,
    resetFilters,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    ticketGridFilterParams,
    ticketGridQueryKey,
    isFilterDebouncing,
    fromTrackerReport,
    reportReturnProjectId,
    trackerReportReturnFilters,
    fromStatusDashboard,
    statusDashboardReturnFilters,
  };
}
